/**
 * Bottom sheet com as sessões de treino concluídas de UM dia (acionado pelo calendário do histórico).
 *
 * Para cada sessão mostra: nome do treino (plano), "duração · nº de exercícios" e, quando o plano
 * pertence a uma rotina, a linha "Rotina: <nome>" — ajuda a identificar de qual rotina o treino veio.
 *
 * Props:
 *   - date: dia selecionado (YYYY-MM-DD); título formatado em pt-BR (null oculta o cabeçalho)
 *   - sessions: SessionSummary[] daquele dia
 *   - visible / onClose: controle do modal
 */
import { View, Text, TouchableOpacity, Modal, StyleSheet } from 'react-native';
import { SessionSummary } from '@/lib/types';

interface Props {
  date: string | null;
  sessions: SessionSummary[];
  visible: boolean;
  onClose: () => void;
}

const DAYS_PT = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const MONTHS_PT = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

function formatDatePT(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return `${DAYS_PT[d.getDay()]}, ${day} de ${MONTHS_PT[month - 1]} de ${year}`;
}

export function WorkoutDayModal({ date, sessions, visible, onClose }: Props) {
  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.sheet} activeOpacity={1} onPress={() => {}}>
          <View style={styles.handle} />
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <Text style={styles.closeBtnText}>← Fechar</Text>
          </TouchableOpacity>
          {date && <Text style={styles.dateText}>{formatDatePT(date)}</Text>}
          {sessions.map(s => (
            <View key={s.id} style={styles.sessionRow}>
              <View style={styles.dot} />
              <View style={{ flex: 1 }}>
                <Text style={styles.planName}>{s.planName}</Text>
                <Text style={styles.sessionMeta}>{s.durationMin} min · {s.exerciseCount} exercícios</Text>
                {s.routineName ? <Text style={styles.routineLabel}>Rotina: {s.routineName}</Text> : null}
              </View>
            </View>
          ))}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: 36 },
  handle: { width: 40, height: 4, borderRadius: 2, backgroundColor: '#ddd', alignSelf: 'center', marginBottom: 16 },
  closeBtn: { marginBottom: 8 },
  closeBtnText: { color: '#C8102E', fontSize: 15, fontWeight: '600' },
  dateText: { fontSize: 16, fontWeight: '700', color: '#1A1A1A', marginBottom: 16 },
  sessionRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  dot: { width: 10, height: 10, borderRadius: 5, backgroundColor: '#C8102E', flexShrink: 0 },
  planName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  sessionMeta: { fontSize: 13, color: '#888', marginTop: 2 },
  routineLabel: { fontSize: 12, color: '#888', marginTop: 3, fontStyle: 'italic' },
});
